import {
  defaultGetChildrenFromNode,
  defaultGetIdFromNode,
  defaultGetLabelFromNode,
} from './defaultGetters';
import { IdType, InternalDataNode, DefaultInputTreeNode } from '../types';

type TreeInputToDFSInternalProps<InputTreeNode extends DefaultInputTreeNode> = {
  denormalizedTree: InputTreeNode[];
  expandedIds?: Record<IdType, boolean>;
  initialExpandAll?: boolean;
  getIdFromNode?: (node: InputTreeNode) => IdType;
  getLabelFromNode?: (node: InputTreeNode) => string;
  getChildrenFromNode?: (node: InputTreeNode) => InputTreeNode[] | undefined;
};
const normalizeTreeInput = <InputTreeNode extends DefaultInputTreeNode>({
  denormalizedTree,
  expandedIds = {},
  initialExpandAll,
  getIdFromNode = defaultGetIdFromNode,
  getLabelFromNode = defaultGetLabelFromNode,
  getChildrenFromNode = defaultGetChildrenFromNode,
}: TreeInputToDFSInternalProps<InputTreeNode>) => {
  const normalizedTree: Record<IdType, InternalDataNode<InputTreeNode>> = {};
  const orderedIds: IdType[] = [];
  const internalExpandedIds: Record<IdType, boolean> = {};

  const recurse = (
    nodes: InputTreeNode[],
    normalizedTree: Record<IdType, InternalDataNode<InputTreeNode>>,
    path: IdType[] = [],
  ): void => {
    let i = 0;
    const length = nodes.length;
    while (i < length) {
      const node = nodes[i++];
      const id = getIdFromNode(node);
      const children = getChildrenFromNode(node);
      const childrenLength = children?.length ?? 0;
      const hasChildren = children && childrenLength > 0;
      const childIds = [];
      if (hasChildren) {
        let index = 0;
        while (index < childrenLength) {
          childIds.push(getIdFromNode(children[index++]));
        }
      }
      const pathLength = path.length;

      const closestParentId = pathLength ? path[pathLength - 1] : null;
      const isParentVisibleAndExpanded =
        !!closestParentId &&
        !!expandedIds[closestParentId] &&
        !!normalizedTree[closestParentId].show;
      normalizedTree[id] = {
        id,
        path,
        childIds,
        label: getLabelFromNode(node),
        show:
          initialExpandAll || !closestParentId || isParentVisibleAndExpanded,
        original: node,
      };
      orderedIds.push(id);

      if (hasChildren) {
        if (initialExpandAll) {
          internalExpandedIds[id] = true;
        }
        path.push(id);
        recurse(children, normalizedTree, [...path]);
        path.pop();
      }
    }
  };

  recurse(denormalizedTree, normalizedTree);
  return {
    normalizedTree,
    orderedIds,
    expandedIds: initialExpandAll ? internalExpandedIds : expandedIds,
  };
};

export default normalizeTreeInput;
